import type { MinMax } from '@iheartjane/types';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import {
  buildRangeWithBounds,
  filterRangeFromSearchParams,
  getFilterStatusInSearchParams,
  searchParamsFromFilterKey,
  searchParamsFromFilterKeyRange,
  singularFilterKey,
} from './utils';

export function useSearchParamFilter(filterKey: string) {
  const [searchParams, setSearchParams] = useSearchParams();

  const singularKey = useMemo(() => singularFilterKey(filterKey), [filterKey]);

  const getFilterOptionValue = useCallback(
    (option: string) =>
      searchParams.getAll(singularKey).find((v) => v === option),
    [singularKey, searchParams]
  );

  const getFilterRangeValue = useCallback(
    () => filterRangeFromSearchParams(searchParams, singularKey),
    [singularKey, searchParams]
  );

  const getFilterRangeValueWithBounds = useCallback(
    (bounds: MinMax) =>
      buildRangeWithBounds(
        bounds,
        filterRangeFromSearchParams(searchParams, singularKey)
      ),
    [singularKey, searchParams]
  );

  const getFilterStatus = useCallback(
    (option: string) =>
      getFilterStatusInSearchParams(searchParams, singularKey, option),
    [singularKey, searchParams]
  );

  const setFilter = useCallback(
    (option: string, value: boolean) => {
      const updated = searchParamsFromFilterKey(
        searchParams,
        singularKey,
        option,
        value
      );
      setSearchParams(updated, {
        preventScrollReset: true,
        replace: true,
      });
    },
    [singularKey, searchParams, setSearchParams]
  );

  const setFilterRange = useCallback(
    (minMax: MinMax) => {
      const updated = searchParamsFromFilterKeyRange(
        searchParams,
        singularKey,
        minMax
      );
      setSearchParams(updated, {
        preventScrollReset: true,
        replace: true,
      });
    },
    [singularKey, searchParams, setSearchParams]
  );

  const clearFilter = useCallback(
    () =>
      setSearchParams(
        (prev) => {
          prev.delete(singularKey);
          return prev;
        },
        { preventScrollReset: true, replace: true }
      ),
    [singularKey, setSearchParams]
  );

  return {
    clearFilter,
    getFilterOptionValue,
    getFilterRangeValue,
    getFilterRangeValueWithBounds,
    getFilterStatus,
    setFilter,
    setFilterRange,
  };
}
